/**
 * Turning what the scanner read into a mould code.
 *
 * What gets engraved on the nameplate is still an open decision - a bare asset
 * code, or a URL that resolves to one. Plates are cut with a laser and cannot be
 * re-cut, so the scanner refuses to depend on that decision: it accepts both,
 * and will keep accepting whatever is already in the field after the decision
 * lands. A tool engraved in 2019 has to scan in 2027.
 *
 * Handled:
 *   MLD-014                                  → MLD-014
 *   https://mouldtrack.…/m/MLD-014           → MLD-014
 *   https://mouldtrack.…/moulds/MLD-014      → MLD-014
 *   https://mouldtrack.…/scan?code=MLD-014   → MLD-014
 *   MOULDTRACK:MLD-014                       → MLD-014
 */
/** Query keys a URL might carry the code under, in the order they are tried. */
const CODE_PARAMS = ['code', 'asset', 'assetCode', 'm', 'id'];
export function normaliseScannedCode(raw) {
    const text = raw.trim();
    if (!text)
        return '';
    // A custom scheme, which is how a QR opens an app rather than a browser.
    const scheme = /^mouldtrack:(?:\/\/)?(.+)$/i.exec(text);
    if (scheme?.[1])
        return scheme[1].trim();
    if (!/^https?:\/\//i.test(text))
        return text;
    try {
        const url = new URL(text);
        for (const key of CODE_PARAMS) {
            const value = url.searchParams.get(key);
            if (value?.trim())
                return value.trim();
        }
        // No parameter, so the code is the last meaningful path segment:
        // /moulds/MLD-014 and /m/MLD-014 both end in the code.
        const segments = url.pathname.split('/').filter(Boolean);
        const last = segments[segments.length - 1];
        if (last)
            return decodeURIComponent(last);
        return text;
    }
    catch {
        // Not a URL after all. Take it at face value rather than losing the read.
        return text;
    }
}
/**
 * Compare codes the way the floor produces them.
 *
 * A code arrives decoded from a QR, typed by a handheld that emulates a
 * keyboard, or thumbed in on a phone. Only the first is reliably exact - the
 * others vary in case and pick up stray spaces. The server already matches
 * case-insensitively; the device's own lookup has to agree, or the app would
 * say "unknown code" about a mould the server then happily scans.
 */
export function codeKey(code) {
    return (code ?? '').trim().toUpperCase();
}
//# sourceMappingURL=scanCode.js.map